import { CatalogUnitOfMeasure } from '@/api/admin-products-types'
import { useCreateUnitOfMeasure, useUpdateUnitOfMeasure } from '@/api/api-mutations'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, FormHint, useAppForm } from '@/components/form/form'
import { RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

export function CatalogUnitForm({ unit }: { unit?: CatalogUnitOfMeasure }) {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const createUnit = useCreateUnitOfMeasure()
	const updateUnit = useUpdateUnitOfMeasure()

	const form = useAppForm({
		defaultValues: {
			code: unit?.code ?? '',
			name: unit?.name ?? '',
			conversionFactor: unit?.conversionFactor == null ? '' : String(unit.conversionFactor),
			isDefaultEach: unit?.isDefaultEach ?? false,
			isDefaultWeight: unit?.isDefaultWeight ?? false
		},
		onSubmit: async ({ value }) => {
			const code = value.code.trim()
			if (!code) {
				toast.error(L.Settings.UnitsOfMeasure.InvalidCode())
				return
			}
			const trimmedQty = value.conversionFactor.trim()
			let conversionFactor: number | undefined
			if (trimmedQty !== '') {
				const parsed = Number(trimmedQty)
				if (!(parsed > 0)) {
					toast.error(L.Settings.UnitsOfMeasure.InvalidQuantity())
					return
				}
				conversionFactor = parsed
			}
			const body = {
				code,
				name: value.name.trim() || undefined,
				conversionFactor,
				isDefaultEach: value.isDefaultEach,
				isDefaultWeight: value.isDefaultWeight
			}
			const result = unit ? await updateUnit({ path: { unitUuid: unit.uuid }, body }) : await createUnit({ body })
			if (result.isSuccess) {
				toast.success(
					unit ? L.Settings.UnitsOfMeasure.EditSuccess() : L.Settings.UnitsOfMeasure.CreateSuccess()
				)
				handleSuccess()
			} else {
				toast.error((result.response as { message?: string })?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>
						{unit ? L.Settings.UnitsOfMeasure.EditTitle() : L.Settings.UnitsOfMeasure.CreateTitle()}
					</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='code'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => (
								<field.TextField
									autoFocus
									label={L.Settings.UnitsOfMeasure.Code()}
									placeholder={L.Settings.UnitsOfMeasure.CodePlaceholder()}
								/>
							)}
						/>
						<form.AppField
							name='name'
							children={field => (
								<field.TextField
									label={L.Settings.UnitsOfMeasure.Name()}
									placeholder={L.Settings.UnitsOfMeasure.NamePlaceholder()}
									optional
								/>
							)}
						/>
						<form.AppField
							name='conversionFactor'
							children={field => (
								<div className='flex flex-col gap-y-1'>
									<field.TextField
										label={L.Settings.UnitsOfMeasure.Quantity()}
										type='number'
										optional
									/>
									<FormHint>{L.Settings.UnitsOfMeasure.QuantityHint()}</FormHint>
								</div>
							)}
						/>
					</FormFieldGroup>
					<FormFieldGroup>
						<form.AppField
							name='isDefaultEach'
							children={field => <field.BooleanField label={L.Settings.UnitsOfMeasure.DefaultEach()} />}
						/>
						<form.AppField
							name='isDefaultWeight'
							children={field => <field.BooleanField label={L.Settings.UnitsOfMeasure.DefaultWeight()} />}
						/>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					{unit ? <form.SaveButton /> : <form.CreateButton />}
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
